import { v4 as uuidv4 } from 'uuid';

import { CLEAR_SITE_DATA } from '../application';
import {
  CREATE_VISUALIZATIONS_SUBSCRIPTION,
  DESTROY_VISUALIZATIONS_SUBSCRIPTION,
  UPSERT_VISUALIZATION,
  DELETE_VISUALIZATION_SUCCESS,
  RECEIVE_VISUALIZATION,
  RECEIVE_VISUALIZATIONS,
  RECEIVED_VALUES,
  INIT_VISUALIZATIONS,
  RESET_VISUALIZATIONS,
  SET_LOADING,
} from './actions';

const initialState = {
  visualizations: {},
  originalVisualizations: {},
  subscriptions: {},
  values: {},
  siteLoaded: undefined,
  hasBeenUpdated: false,
};

// eslint-disable-next-line default-param-last
export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case RESET_VISUALIZATIONS: {
      return {
        ...state,
        visualizations: { ...state.originalVisualizations },
        originalVisualizations: { ...state.originalVisualizations },
      };
    }
    case UPSERT_VISUALIZATION: {
      const { visualization } = action;

      return {
        ...state,
        visualizations: {
          ...state.visualizations,
          [visualization.id]: {
            ...(state.visualizations[visualization.id] || {}),
            ...visualization,
          },
        },
      };
    }
    case RECEIVE_VISUALIZATION: {
      const { visualization } = action;

      return {
        ...state,
        visualizations: {
          ...state.visualizations,
          [visualization.id]: { ...visualization },
        },
        originalVisualizations: {
          ...state.originalVisualizations,
          [visualization.id]: { ...visualization },
        },
      };
    }
    case RECEIVE_VISUALIZATIONS: {
      const { visualizations, siteId } = action;
      const indexed = visualizations.reduce((acc, v) => ({ ...acc, [v.id]: v }), {});

      return {
        ...state,
        visualizations: { ...indexed },
        originalVisualizations: { ...indexed },
        siteLoaded: siteId || state.siteLoaded,
      };
    }
    case DELETE_VISUALIZATION_SUCCESS: {
      const { visualizationId } = action;
      const vizCopy = { ...state.visualizations };
      const origVizCopy = { ...state.originalVisualizations };
      delete vizCopy[visualizationId];
      delete origVizCopy[visualizationId];

      return {
        ...state,
        visualizations: vizCopy,
        originalVisualizations: origVizCopy,
      };
    }
    case RECEIVED_VALUES: {
      const {
        visualizationId,
        latestValues: newLatestValues,
        seriesValues: newSeriesValues,
        panelId,
      } = action;

      const { values } = state;
      const updated = (values[visualizationId] || []).map((item) => {
        if (item.panelId === panelId) {
          const { latestValues, seriesValues } = item;
          return {
            panelId,
            latestValues: newLatestValues || latestValues,
            seriesValues: newSeriesValues || seriesValues,
            loading: false,
            initialLoaded: true,
          };
        }
        return item;
      });

      return {
        ...state,
        values: {
          ...values,
          [visualizationId]: updated,
        },
      };
    }
    case SET_LOADING: {
      const { visualizationId, loading, panelId } = action;
      const { values } = state;
      const vizValues = values[visualizationId];
      if (vizValues) {
        const updated = vizValues.map((item) => {
          if (item.panelId === panelId) {
            return { ...item, loading };
          }
          return item;
        });

        return {
          ...state,
          values: {
            ...values,
            [visualizationId]: updated,
          },
        };
      }
      return state;
    }
    case CREATE_VISUALIZATIONS_SUBSCRIPTION: {
      const { visualizationId, panelId } = action;
      const { subscriptions, values } = state;
      const newVizSub = [...(subscriptions[visualizationId] || [])];
      const newValues = [...(values[visualizationId] || [])];
      const newSub = { panelId, subId: uuidv4() };
      const value = {
        values: null,
        initialLoaded: false,
        loading: false,
        panelId,
      };
      const valueExists = newValues.find((v) => v.panelId === panelId);
      const subExists = newVizSub.find((v) => v.panelId === panelId);

      if (!valueExists) {
        newValues.push(value);
      }
      if (!subExists) {
        newVizSub.push(newSub);
      }

      return {
        ...state,
        subscriptions: {
          ...subscriptions,
          [visualizationId]: newVizSub,
        },
        values: {
          ...values,
          [visualizationId]: newValues,
        },
      };
    }
    case DESTROY_VISUALIZATIONS_SUBSCRIPTION: {
      const { visualizationId, panelId } = action;
      const { subscriptions } = state;
      const { [visualizationId]: viz, ...rest } = subscriptions;

      const vizPanelIdIndex = viz?.findIndex((p) => p.panelId === panelId);

      if (vizPanelIdIndex > -1) {
        viz.splice(vizPanelIdIndex, 1);
        if (viz.length > 0) {
          rest[visualizationId] = viz;
        }
      }

      return {
        ...state,
        subscriptions: {
          ...rest,
        },
      };
    }
    case INIT_VISUALIZATIONS:
    case CLEAR_SITE_DATA: {
      // ***IMPORTANT***
      // Explicitly resetting each piece of state here because we've experienced
      // issues with stale state (in visualizations, specifically) - even when returning
      // initialState, using a spread copy of initialState as default state,
      // and/or returning a spread copy of initialState.
      return {
        ...state,
        visualizations: {},
        originalVisualizations: {},
        subscriptions: {},
        values: {},
        hasBeenUpdated: false,
        siteLoaded: undefined,
      };
    }
    default:
      return state;
  }
};

export default reducer;
